import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import {
  Settings as SettingsIcon, Moon, Sun, PlayCircle, Volume2, Trash2, Download,
  Save, Check, ImageOff, UserX, ShieldCheck, ScrollText, Type, Phone,
  Accessibility, Languages, MousePointer2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { SHOW_AUTH } from '../config/flags';
import { readPref } from '../utils/localSettings';

const STORAGE_KEY = 'vaad_settings_v1';

const DEFAULTS = {
  // Feb-2026 (user: "dark mode is turned on just by going into settings"):
  // default MUST be light — dark was auto-toggling for every first-time
  // Settings visitor because loadSettings returns DEFAULTS when there's
  // no saved settings blob, and the effect on line ~145 immediately
  // applies whichever theme value it sees.
  theme: 'light',           // 'dark' | 'light'
  autoplayNext: true,      // play next in queue when current ends
  defaultPlaybackRate: 1,  // 0.75, 1, 1.25, 1.5, 2
  defaultVolume: 0.9,      // 0..1
  keepHistoryDays: 60,     // 7, 30, 60, 180, 0 (forever)
  downloadFormat: 'original', // 'original' | 'mp3'
  compactSpeakerGrid: false,
  // Feb-2026 (user): "there will be users on cats quins and other newer
  // dumbphones — make a setting that can adjust it to best these phones
  // or even to make everything bigger or fit screen." Scales root font
  // size and forces certain non-critical UI to hide/simplify on 'huge'.
  textScale: 'normal',     // 'normal' | 'large' | 'huge'
  // Feb-2026 (user): "Add a Settings toggle that swaps in a keyboard-navigable,
  // low-JS layout for feature-phone browsers (Cat/Quin/KaiOS)." When ON we
  // apply a `.vaad-simple-mode` class to <html> — global CSS then disables
  // animations, drops gradients + shadows, forces high-contrast text, and
  // makes every clickable element at least 44px tall so it's reachable
  // with a directional pad. Persists per-device like the rest of Settings.
  simpleMode: false,
  // ON by default — showing the speaker's face on every card is the whole
  // point of the design. Advanced users can flip it off if they want to
  // scroll faster with plain generic covers.
  showSpeakerPortraits: true,
};

export const SPEAKER_PORTRAITS_SETTING_EVENT = 'vaad-setting-speaker-portraits-changed';

/** Read the "show speaker portraits" toggle from anywhere in the app. */
export const getShowSpeakerPortraitsSetting = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS.showSpeakerPortraits;
    const parsed = JSON.parse(raw);
    return parsed.showSpeakerPortraits !== false; // any explicit false disables
  } catch {
    return DEFAULTS.showSpeakerPortraits;
  }
};

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
};

const saveSettings = (s) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
};

const Row = ({ icon: Icon, label, sub, children }) => (
  <div className="bg-white border border-[#e5e0d5] rounded-xl p-4 md:p-5 flex items-start gap-4">
    <div className="w-9 h-9 rounded-lg bg-[#ffffff] flex items-center justify-center text-[#9a7b24] shrink-0">
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[#0f1729] font-semibold text-sm">{label}</p>
      {sub && <p className="text-[#8a8f99] text-xs mt-0.5">{sub}</p>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const Toggle = ({ checked, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`w-11 h-6 rounded-full p-0.5 transition-colors ${
      checked ? 'bg-[#9a7b24]' : 'bg-[#c9beac]'
    }`}
  >
    <span
      className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

const Select = ({ value, onChange, options, testid }) => (
  <select
    value={value}
    onChange={(e) => {
      const v = e.target.value;
      const n = Number(v);
      onChange(Number.isNaN(n) || String(n) !== v ? v : n);
    }}
    className="bg-white border border-[#e5e0d5] rounded-md px-2.5 py-1.5 text-sm text-[#0f1729] focus:outline-none focus:ring-1 focus:ring-[#9a7b24]/60"
    data-testid={testid}
  >
    {options.map((o) => (
      <option key={String(o.value)} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const SettingsPage = () => {
  const [settings, setSettings] = useState(loadSettings);
  const [savedFlash, setSavedFlash] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Feb-2026 (#25, #26, #29) — reading prefs live in a separate namespace
  // (utils/localSettings.js). Mirror into local state so the toggles
  // rerender immediately when clicked.
  const [sephardic, setSephardic] = useState(() => readPref.sephardic);
  const [highContrast, setHighContrast] = useState(() => readPref.highContrast);
  const [handsOff, setHandsOff] = useState(() => readPref.handsOff);

  const deleteAccount = async () => {
    if (!window.confirm(
      'Permanently delete your account?\n\nThis removes your profile, email subscriptions, and playlists from our servers. This cannot be undone.'
    )) return;
    if (!window.confirm('Last check — are you sure you want to permanently delete your account?')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/account`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        try {
          Object.keys(localStorage).forEach((k) => { if (k.startsWith('vaad_')) localStorage.removeItem(k); });
        } catch { /* ignore */ }
        toast.success('Your account has been permanently deleted.');
        setTimeout(() => { window.location.href = '/'; }, 1200);
      } else if (res.status === 401) {
        toast.error("You're not signed in — there is no account to delete on this device.");
      } else {
        toast.error('Something went wrong deleting your account. Please try again or email us.');
      }
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Persist on every change and reflect theme immediately
  useEffect(() => {
    saveSettings(settings);
    document.documentElement.classList.toggle('dark', settings.theme === 'dark');
    localStorage.setItem('vaad_theme', settings.theme);
    // Apply text-scale to the root element so every rem-based size scales.
    const html = document.documentElement;
    html.classList.remove('vaad-text-large', 'vaad-text-huge');
    if (settings.textScale === 'large') html.classList.add('vaad-text-large');
    else if (settings.textScale === 'huge') html.classList.add('vaad-text-huge');
    // Simple / feature-phone mode: applied at the root so it wins over any
    // component-scoped styling. Global CSS in index.css does the heavy lifting.
    html.classList.toggle('vaad-simple-mode', Boolean(settings.simpleMode));
    try { window.dispatchEvent(new Event(SPEAKER_PORTRAITS_SETTING_EVENT)); } catch { /* ignore */ }
  }, [settings]);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const clearAll = () => {
    if (!window.confirm('Clear all local history, favorites, pinned speakers, and settings?\nRecordings themselves are not affected.')) return;
    ['vaad_history_v1', 'vaad_history_pics_v1', 'vaad_resume_v1', 'vaad_favorites_v1',
     'vaad_pinned_speakers_v1', STORAGE_KEY].forEach((k) => {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
    });
    toast.success('Local data cleared. Reloading…');
    setTimeout(() => window.location.reload(), 900);
  };

  const flashSaved = () => {
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1200);
  };

  const resetDefaults = () => {
    setSettings({ ...DEFAULTS });
    toast.success('Settings reset to defaults');
    flashSaved();
  };

  return (
    <div className="flex h-screen bg-[#ffffff] overflow-hidden">
      <Sidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6 md:p-8">
          <div className="flex items-center gap-2 mb-1">
            <SettingsIcon className="w-6 h-6 text-[#9a7b24]" />
            <h1 className="text-4xl font-bold text-[#0f1729]">Settings</h1>
          </div>
          <p className="text-[#4b5563] text-sm mb-6 max-w-xl">
            Personalize how The Vaad Otzar looks and plays. Everything below is saved to this device only.
          </p>

          <div className="space-y-3">
            <Row
              icon={settings.theme === 'dark' ? Moon : Sun}
              label="Theme"
              sub="Dark uses the deep-navy / gold heritage palette. Light is cream-and-ink."
            >
              <Select
                value={settings.theme}
                onChange={(v) => update({ theme: v })}
                options={[
                  { value: 'dark', label: 'Dark' },
                  { value: 'light', label: 'Light' },
                ]}
                testid="setting-theme"
              />
            </Row>

            <Row
              icon={Type}
              label="Text size"
              sub="Best for KaiOS / feature phones or anyone who prefers bigger text. 'Huge' also hides secondary chrome."
            >
              <Select
                value={settings.textScale}
                onChange={(v) => update({ textScale: v })}
                options={[
                  { value: 'normal', label: 'Normal' },
                  { value: 'large', label: 'Large' },
                  { value: 'huge', label: 'Huge (feature phone)' },
                ]}
                testid="setting-text-scale"
              />
            </Row>

            <Row
              icon={Phone}
              label="Simple mode (Cat / Quin / feature phones)"
              sub="Big tap targets, no animations, no gradients, no shadows. Every button is keyboard-focusable so the D-pad can navigate the whole app."
            >
              <Toggle
                checked={settings.simpleMode}
                onChange={(v) => update({ simpleMode: v })}
                testid="setting-simple-mode"
              />
            </Row>

            {/* Feb-2026 (#25) — Sephardic pronunciation toggle. */}
            <Row
              icon={Languages}
              label="Sephardic transliteration"
              sub={`When ON, transliterated Hebrew is rendered in the Sephardic ("Shabbat", "mitzvot") style instead of Ashkenaz ("Shabbos", "mitzvos"). Applies to the transcript vocabulary panel.`}
            >
              <Toggle
                checked={sephardic}
                onChange={(v) => { readPref.sephardic = v; setSephardic(v); }}
                testid="setting-sephardic"
              />
            </Row>

            {/* Feb-2026 (#26) — Yellow-on-black high-contrast mode. */}
            <Row
              icon={Accessibility}
              label="High-contrast mode"
              sub="Yellow-on-black chrome for the transcript reader. Recommended for low-vision learners; keeps the rest of the app usable at a distance."
            >
              <Toggle
                checked={highContrast}
                onChange={(v) => { readPref.highContrast = v; setHighContrast(v); }}
                testid="setting-high-contrast"
              />
            </Row>

            {/* Feb-2026 (#29) — Hands-off auto-scroll while listening. */}
            <Row
              icon={MousePointer2}
              label="Hands-off transcript scroll"
              sub="Keeps the currently-spoken word perfectly centered on the screen so you never touch the phone while listening. Great for kollel-time learning."
            >
              <Toggle
                checked={handsOff}
                onChange={(v) => { readPref.handsOff = v; setHandsOff(v); }}
                testid="setting-hands-off"
              />
            </Row>

            <Row
              icon={PlayCircle}
              label="Auto-play next recording"
              sub="When a shiur ends, automatically start the next one from the queue."
            >
              <Toggle
                checked={settings.autoplayNext}
                onChange={(v) => update({ autoplayNext: v })}
                testid="setting-autoplay"
              />
            </Row>

            <Row
              icon={PlayCircle}
              label="Default playback speed"
              sub="Applied to every new shiur you open."
            >
              <Select
                value={settings.defaultPlaybackRate}
                onChange={(v) => update({ defaultPlaybackRate: v })}
                options={[
                  { value: 0.75, label: '0.75×' },
                  { value: 1, label: '1×' },
                  { value: 1.25, label: '1.25×' },
                  { value: 1.5, label: '1.5×' },
                  { value: 1.75, label: '1.75×' },
                  { value: 2, label: '2×' },
                ]}
                testid="setting-speed"
              />
            </Row>

            <Row
              icon={Volume2}
              label="Default volume"
              sub="Where the volume slider starts when the player first loads."
            >
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={settings.defaultVolume}
                onChange={(e) => update({ defaultVolume: Number(e.target.value) })}
                className="w-32 accent-[#9a7b24]"
                data-testid="setting-volume"
              />
            </Row>

            <Row
              icon={Download}
              label="Download format"
              sub="Original keeps the source file. MP3 uses the audio converter if the source is video."
            >
              <Select
                value={settings.downloadFormat}
                onChange={(v) => update({ downloadFormat: v })}
                options={[
                  { value: 'original', label: 'Original' },
                  { value: 'mp3', label: 'MP3 (audio only)' },
                ]}
                testid="setting-download-format"
              />
            </Row>

            <Row
              icon={PlayCircle}
              label="Compact speaker grid"
              sub="Fits more speaker portraits per row on the homepage."
            >
              <Toggle
                checked={settings.compactSpeakerGrid}
                onChange={(v) => update({ compactSpeakerGrid: v })}
                testid="setting-compact-grid"
              />
            </Row>

            <Row
              icon={ImageOff}
              label="Show speaker photos on recording tiles"
              sub="ON is recommended — cards are more recognizable at a glance. Turn OFF to scroll faster with plain covers (not recommended)."
            >
              <Toggle
                checked={settings.showSpeakerPortraits}
                onChange={(v) => update({ showSpeakerPortraits: v })}
                testid="setting-speaker-portraits"
              />
            </Row>

            <Row
              icon={Trash2}
              label="Keep listening history for"
              sub="Older entries are auto-pruned. Recordings themselves are untouched."
            >
              <Select
                value={settings.keepHistoryDays}
                onChange={(v) => update({ keepHistoryDays: v })}
                options={[
                  { value: 7, label: '7 days' },
                  { value: 30, label: '30 days' },
                  { value: 60, label: '60 days' },
                  { value: 180, label: '6 months' },
                  { value: 0, label: 'Forever' },
                ]}
                testid="setting-history-days"
              />
            </Row>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={resetDefaults}
              className="px-4 py-2 rounded-md text-sm font-semibold text-[#0f1729] bg-white border border-[#e5e0d5] hover:border-[#9a7b24]"
              data-testid="setting-reset"
            >
              Reset to defaults
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#8a1a1a] hover:bg-[#6f1414] inline-flex items-center gap-1.5"
              data-testid="setting-clear-local"
            >
              <Trash2 className="w-4 h-4" /> Clear all local data
            </button>
            <div className={`ml-auto inline-flex items-center gap-1.5 text-[12px] font-semibold ${savedFlash ? 'text-[#2f7a2f]' : 'text-[#8a8f99]'}`}>
              {savedFlash ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
              {savedFlash ? 'Saved' : 'Auto-saved to this device'}
            </div>
          </div>

          <div className="mt-10 bg-white border border-[#e8c9c9] rounded-xl p-4 md:p-5" data-testid="setting-account-danger-zone" style={{ display: SHOW_AUTH ? undefined : 'none' }}>
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-lg bg-[#fdf2f2] flex items-center justify-center text-[#8a1a1a] shrink-0">
                <UserX className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#0f1729] font-semibold text-sm">Delete my account</p>
                <p className="text-[#8a8f99] text-xs mt-0.5">
                  Permanently removes your profile, email subscriptions, and playlists from our
                  servers. The library itself stays free to use without an account.
                </p>
              </div>
              <button
                type="button"
                onClick={deleteAccount}
                disabled={deleting}
                className="shrink-0 px-4 py-2 rounded-md text-sm font-semibold text-[#8a1a1a] bg-white border border-[#e0b4b4] hover:bg-[#8a1a1a] hover:text-white transition-colors disabled:opacity-50"
                data-testid="setting-delete-account"
              >
                {deleting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-4 text-xs text-[#8a8f99]">
            <Link to="/privacy" className="inline-flex items-center gap-1 hover:text-[#9a7b24]" data-testid="settings-privacy-link">
              <ShieldCheck className="w-3.5 h-3.5" /> Privacy Policy
            </Link>
            <Link to="/terms" className="inline-flex items-center gap-1 hover:text-[#9a7b24]" data-testid="settings-terms-link">
              <ScrollText className="w-3.5 h-3.5" /> Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
